import React, { useState, useCallback } from 'react';
import './BlurUpImage.css';

export interface BlurUpImageProps {
  /** Original PNG src (fallback for browsers without WebP) */
  src: string;
  /** WebP src (preferred format) */
  webpSrc: string;
  /** Base64 data URI for the tiny blurred placeholder */
  placeholderSrc: string;
  /** Dominant color hex (e.g. "#1a1a2e") for instant background fill */
  dominantColor?: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  /** Override loading strategy. Default: "lazy" */
  loading?: 'lazy' | 'eager';
  decoding?: 'async' | 'auto' | 'sync';
}

export function BlurUpImage({
  src,
  webpSrc,
  placeholderSrc,
  dominantColor,
  alt,
  className,
  width,
  height,
  loading = 'lazy',
  decoding = 'async',
}: BlurUpImageProps): React.ReactElement {
  const [loaded, setLoaded] = useState(false);
  const handleLoad = useCallback(() => setLoaded(true), []);

  return (
    <div
      className={`blur-up ${loaded ? 'blur-up--loaded' : ''} ${className ?? ''}`}
      style={dominantColor ? { backgroundColor: dominantColor } : undefined}
    >
      {/* Blurred placeholder — always visible until full image loads */}
      <img
        src={placeholderSrc}
        alt=""
        aria-hidden="true"
        className="blur-up__placeholder"
        width={width}
        height={height}
      />
      {/* Full-resolution image with WebP source */}
      <picture>
        <source srcSet={webpSrc} type="image/webp" />
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          loading={loading}
          decoding={decoding}
          onLoad={handleLoad}
          className="blur-up__full"
        />
      </picture>
    </div>
  );
}
